import {
  ColumnDef,
  PaginationState,
  TableOptions,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import { FormatContent, useContent } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import { useSeatingColumnSorting } from 'src/tables/common/hooks/useSeatingColumnSorting';
import {
  defaultRecentSalesV2Columns,
  MERGED_RECENT_SALES_TABLE_COLUMNS_CONFIG,
} from 'src/tables/SalesTable/configs/RecentSaleTableColumnsConfig';
import { SaleWithEvent } from 'src/tables/SalesTable/SalesTable.type';
import { NoData, Table } from 'src/tables/Table';
import { RowWrapper } from 'src/tables/Table/Table.types';
import { getRecentSalesColumnConfigById } from 'src/utils/columns/columnUtils';
import {
  RecentSalesTableColumnId,
  SalesTableColumnId,
} from 'src/utils/columns/sales/salesColumnUtils.types';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { SectionType } from 'src/utils/types/sectionType';
import { Feature, Sale, UserSetting } from 'src/WebApiController';

const PAGE_SIZE = 10;

export const RecentSalesTableV2 = ({
  data,
  onRowWrapper,
}: {
  data: Sale[];
  onRowWrapper: RowWrapper<SaleWithEvent | null>;
}) => {
  const { data: catalog, isLoading: isCatalogLoading } =
    useCatalogDataContext();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const salesText = useContent(ContentId.Sales);
  const { isDarkMode } = useSiteTheme();
  const hasPurchasedByColumnFeature = useUserHasFeature(
    Feature.RecentSalesV2PurchasedByColumn
  );

  const seatingColumnSorting = useSeatingColumnSorting<SaleWithEvent>(
    SalesTableColumnId.Seating,
    SectionType.Sales
  );

  const filteredData = useMemo(
    () =>
      data
        .map((sale) => {
          const { event, performer, venue } = getEventPerformerVenue(
            sale.viagVirtId,
            catalog
          );

          if (event) {
            return { event: event.event, sale, performer, venue };
          }

          return null;
        })
        ?.filter((s) => s != null)
        ?.map((s) => s!),
    [catalog, data]
  );

  const defaultColumns = useMemo(
    () =>
      hasPurchasedByColumnFeature
        ? defaultRecentSalesV2Columns
        : defaultRecentSalesV2Columns.filter(
            (col) => col !== RecentSalesTableColumnId.PurchasedBy
          ),
    [hasPurchasedByColumnFeature]
  );

  const {
    value: storedRecentSalesTableColumnOrderSetting = defaultColumns,
    setUserSetting: setRecentSalesTableColumnsOrderSetting,
  } = useServerUserSetting<string[]>({
    id: UserSetting.RecentSalesTableColumnsOrder,
  });

  const { value: storedRecentSalesTableColumnsEnabledSetting } =
    useServerUserSetting<string[]>({
      id: UserSetting.RecentSalesTableColumnsEnabled,
    });

  const displayedColumnsConfig = useMemo(() => {
    const saleColumnOrder = storedRecentSalesTableColumnOrderSetting
      .filter(
        // If storedSaleColumnsEnabledSetting has never been set, default to true
        (c) => storedRecentSalesTableColumnsEnabledSetting?.includes(c) ?? true
      )
      .filter(
        // Filter out PurchasedBy column if feature flag is disabled
        (c) =>
          hasPurchasedByColumnFeature ||
          c !== RecentSalesTableColumnId.PurchasedBy
      );

    const salesTableColumnsConfig = Object.values(
      MERGED_RECENT_SALES_TABLE_COLUMNS_CONFIG
    );

    const columns = new Set([
      ...salesTableColumnsConfig
        .filter((column) => {
          const columnDef = getRecentSalesColumnConfigById(
            column.id as SalesTableColumnId
          ).personalization;
          return !columnDef.isConfigurable;
        })
        .map((item) => item.id as RecentSalesTableColumnId),
      ...saleColumnOrder,
    ]);

    return Array.from(columns).reduce<ColumnDef<SaleWithEvent | null>[]>(
      (acc, columnId) => {
        // Filter out PurchasedBy column if feature flag is disabled
        if (
          !hasPurchasedByColumnFeature &&
          columnId === RecentSalesTableColumnId.PurchasedBy
        ) {
          return acc;
        }
        const columnConfig = salesTableColumnsConfig.find(
          (column) => column.id === columnId
        );
        if (columnConfig) acc.push(columnConfig);
        return acc;
      },
      []
    );
  }, [
    storedRecentSalesTableColumnOrderSetting,
    storedRecentSalesTableColumnsEnabledSetting,
    hasPurchasedByColumnFeature,
  ]);

  const options: Partial<TableOptions<SaleWithEvent | null>> = useMemo(
    () => ({
      data: filteredData,
      columns: displayedColumnsConfig,
      state: {
        pagination,
        columnPinning: {
          right: [RecentSalesTableColumnId.Actions],
        },
      },
      onPaginationChange: setPagination,
      sortingFns: { ...seatingColumnSorting },
      meta: {
        sectionType: SectionType.RecentSalesTable,
      },
    }),
    [filteredData, pagination, seatingColumnSorting, displayedColumnsConfig]
  );

  if (isCatalogLoading) {
    return <PosSpinner />;
  }

  if (!filteredData.length) {
    return (
      <NoData>
        <FormatContent
          id={FormatContentId.NoDataAvailable}
          params={salesText}
        />
      </NoData>
    );
  }

  return (
    <ColumnResizingContextProvider
      userSettingId={UserSetting.RecentSalesTableColumnsWidths}
    >
      <Table
        options={options}
        rowWrapper={onRowWrapper}
        showPageSizeOptions={false}
        disableFooter
        tableRowStyle={
          isDarkMode
            ? {
                backgroundColor: vars.color.backgroundPrimary,
                borderColor: vars.color.border,
              }
            : undefined
        }
        isV2FooterVariant
      />
    </ColumnResizingContextProvider>
  );
};
